// @ts-strict-ignore
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { ProjectModel } from '@project/model/project.model';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { CompositeArticleInformation } from '@article/model/composite-article-information';
import { Injectable } from '@angular/core';
import { CompositeArticleInfoUtils } from './composite-article-info-utils';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';

@Injectable({ providedIn: 'root' })
export class CompositeArticleUtils {
  public static readonly COMPOSITE_TYPE_SINGLE_BASE: string = 'compositeSingleBaseArticle';
  public static readonly COMPOSITE_TYPE_MULTIPLE_BASE: string = 'compositeMultipleBaseArticle';

  constructor(
    private projectModel: ProjectModel,
    private articleConnectionService: ArticleConnectionService,
    private containerService: ContainerService,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
  ) {
  }

  public getCompositeArticleType(compositeArticle: ArticleVO): string {
    return ArticleUtils.getArticleBehaviorPropertyStringValue(compositeArticle, ArticleBehaviorPropertyDef.COMPOSITE_ARTICLE_TYPE);
  }

  public getCompositeArticleUsageIdsFromUsageByIndex(articleUsage: ArticleUsageVO, index: number): string {
    const usageIds: string[] = ArticleUsageUtils.getArticleUsageInfoPropertyStringValues(
      articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID
    );
    return usageIds[index];
  }

  public getCompositeArticleNo(compositePartArticle: ArticleContainer): string {
    if (!compositePartArticle) {
      return null;
    }
    return this.compositeArticleInfoUtils.getCompositeArticleNoFromUsage(compositePartArticle.articleUsage);
  }

  public getCompositeArticleNoFromUsageByIndex(articleUsage: ArticleUsageVO, index: number): string {
    const values: string[] = ArticleUsageUtils.getArticleUsageInfoPropertyStringValues(
      articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO
    );
    return values[index];
  }

  public getCompositePartUsageId(compositePartArticle: ArticleContainer, useStrippedCompositeInfo: boolean = false): string {
    return this.getCompositePartUsageIdFromUsage(compositePartArticle.articleUsage, useStrippedCompositeInfo);
  }

  public getCompositePartUsageIdFromUsage(articleUsage: ArticleUsageVO, useStrippedCompositeInfo: boolean = false): string {
    return ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      articleUsage,
      useStrippedCompositeInfo ? ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_PART_USAGE_ID : ArticleUsageInfoPropertyDef.COMPOSITE_PART_USAGE_ID
    );
  }

  public getCompositeArticle(compositePartArticleContainer: ArticleContainer, useStrippedCompositeInfo: boolean = false): ArticleVO {
    if (!compositePartArticleContainer) {
      return null;
    }
    return this.compositeArticleInfoUtils.getCompositeArticleFromUsage(compositePartArticleContainer.articleUsage, useStrippedCompositeInfo);
  }

  public isCompositePartArticleSelectionAllowed(compositePartArticleContainer: ArticleContainer): boolean {
    const compositeArticle: ArticleVO = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(compositePartArticleContainer);
    if (!compositeArticle) {
      return false;
    }
    if (PropertyUtils.containsProperty(compositeArticle.behaviorProperties, ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_SELECTION_ALLOWED)) {
      return PropertyUtils.getBooleanPropertyValue(
        compositeArticle.behaviorProperties,
        ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_SELECTION_ALLOWED
      );
    }
    // if there is no such behavior property in the composite article, then the default value is true
    return true;
  }

  public isCompositePartArticleModificationAllowed(compositePartArticleContainer: ArticleContainer): boolean {
    const compositeArticle: ArticleVO = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(compositePartArticleContainer);
    if (!compositeArticle) {
      return false;
    }
    return this.isCompositeArticleModificationAllowed(compositeArticle);
  }

  public isCompositeArticleModificationAllowed(compositeArticleVO: ArticleVO): boolean {
    if (!compositeArticleVO) {
      return false;
    }
    if (PropertyUtils.containsProperty(compositeArticleVO.behaviorProperties, ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_MODIFICATION_ALLOWED)) {
      return PropertyUtils.getBooleanPropertyValue(
        compositeArticleVO.behaviorProperties,
        ArticleBehaviorPropertyDef.IS_COMPOSITE_PART_MODIFICATION_ALLOWED
      );
    }
    // if there is no such behavior property in the composite article, then the default value is true
    return true;
  }
  public getCompositePartArticleUsagesByIndex(compositePartArticle: ArticleContainer, index: number): ArticleUsageVO[] {
    const compositeArticleUsageId: string = this.getCompositeArticleUsageIdsFromUsageByIndex(compositePartArticle.articleUsage, index);
    if (compositeArticleUsageId) {
      return this.getCompositePartArticleUsagesByIdByIndex(compositeArticleUsageId, index);
    } else {
      return [];
    }
  }

  public getCompositePartArticleUsagesByIdByIndex(compositeArticleUsageId: string, index: number): ArticleUsageVO[] {
    if (!compositeArticleUsageId) {
      return [];
    }

    const result: ArticleUsageVO[] = [];
    for (const articleUsage of this.projectModel.getAllArticleUsages()) {
      const compArticleUsage: string = this.getCompositeArticleUsageIdsFromUsageByIndex(articleUsage, index);
      if (compArticleUsage === compositeArticleUsageId) {
        result.push(articleUsage);
      }
    }
    return result;
  }

  public getBaseCompositePartArticleUsageIdByArticleUsageId(compositeArticleId: string, useStrippedCompositeInfo: boolean = false): string {
    const usages: ArticleUsageVO[] = this.projectModel.getCompositePartArticleUsagesById(compositeArticleId);
    if (!usages || usages.length === 0) {
      return compositeArticleId;
    } else {
      return this.getBaseCompositePartArticlesByArticleUsage(usages[0], useStrippedCompositeInfo)[0].articleUsage.id;
    }
  }

  public getCompositePartArticleUsageIdsByIndex(compositePartArticle: ArticleContainer, index: number): string[] {
    const result: string[] = [];
    for (const articleUsage of this.getCompositePartArticleUsagesByIndex(compositePartArticle, index)) {
      result.push(articleUsage.id);
    }
    return result;
  }

  public getBaseCompositePartArticlesByArticleUsage(compositePartUsage: ArticleUsageVO, useStrippedComposition: boolean = false): ArticleContainer[] {
    const compositePartArticle: ArticleContainer = this.containerService.findArticleContainerInGlobal(compositePartUsage.id);
    return this.containerService.getBaseCompositePartArticles(compositePartArticle, useStrippedComposition);
  }

  public isAllCompositePartsIncluded(compositePartArticleUsageIds: string[], sourceArticleUsageIds: string[]): boolean {
    for (const compositePartArticleUsageId of compositePartArticleUsageIds) {
      if (sourceArticleUsageIds.indexOf(compositePartArticleUsageId) === -1) {
        return false;
      }
    }
    return true;
  }

  public isAllBaseCompositePartsOfArticleIncluded(container: ArticleContainer, sourceArticleUsageIds: string[]): boolean {
    if (!container || ArrayUtils.isEmpty(sourceArticleUsageIds)) {
      return false;
    }

    const baseCompositeContainers: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(container);
    const compositeBaseIds: string[] = ArticleContainerUtils.extractArticleGUIDs(baseCompositeContainers);
    return this.isAllCompositePartsIncluded(compositeBaseIds, sourceArticleUsageIds);
  }

  public static setCompositePartUsageIdToUsage(articleUsage: ArticleUsageVO, partUsageId: string): void {
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_PART_USAGE_ID, partUsageId);
  }

  /**
   * Shifts composite article information from the given usage and applies it as stripped info to this usage.
   */
  public static stripCompositeArticleInformationFromUsage(articleUsage: ArticleUsageVO): void {
    const shiftedCompositeInformation: CompositeArticleInformation = this.shiftCompositeArticleInformationFromUsage(articleUsage);
    if (shiftedCompositeInformation) {
      this.applyStrippedCompositeArticleInformation(articleUsage, shiftedCompositeInformation);
    }
  }

  /**
   * Removes actual composite information from the given article-usage.
   * This basically identical to stripping of complex article information during creation of mcs/gds project.
   *
   * NOTE:
   * 1. group-id is removed from the given article-usage (but it's included in the result).
   * 2. compositePartUsageId property (original usage-id) is shifted, so this property won't exist in the given article-usage anymore
   * (since there is no stack - just single value).
   *
   * @param articleUsage
   * @return
   */
  public static shiftCompositeArticleInformationFromUsage(articleUsage: ArticleUsageVO): CompositeArticleInformation {
    if (!articleUsage || !ArticleUsageUtils.isCompositePartArticleUsage(articleUsage, false)) {
      return null;
    }
    const shiftedCompositeInformation: CompositeArticleInformation = new CompositeArticleInformation(
      ArticleUsageUtils.shiftArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID),
      ArticleUsageUtils.shiftArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID),
      ArticleUsageUtils.shiftArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION),
      ArticleUsageUtils.shiftArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO),
      ArticleUsageUtils.shiftArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_PART_USAGE_ID),
      articleUsage.groupId
    );
    articleUsage.groupId = null;
    return shiftedCompositeInformation;
  }

  public static copyCompositeArticleInformationFromUsage(
    sourceArticleUsage: ArticleUsageVO,
    targetArticleUsage: ArticleUsageVO,
    compositePartUsageId: string = null
  ): void {
    if (!sourceArticleUsage || !targetArticleUsage) {
      return;
    }
    if (!ArticleUsageUtils.isCompositePartArticleUsage(sourceArticleUsage, false)) {
      return;
    }
    const compositeInformation: CompositeArticleInformation = new CompositeArticleInformation(
      ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID),
      ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID),
      ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION),
      ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO),
      compositePartUsageId,
      sourceArticleUsage.groupId
    );
    this.applyCompositeArticleInformation(targetArticleUsage, compositeInformation);
  }

  public static copyStrippedCompositeArticleInformationFromUsage(
    sourceArticleUsage: ArticleUsageVO,
    targetArticleUsage: ArticleUsageVO,
    compositePartUsageId: string = null
  ): void {
    if (!sourceArticleUsage || !targetArticleUsage) {
      return;
    }
    if (!ArticleUsageUtils.isCompositePartArticleUsage(sourceArticleUsage, true)) {
      return;
    }
    const strippedCompositeInformation: CompositeArticleInformation = new CompositeArticleInformation(
      ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_USAGE_ID),
      ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID),
      ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_VERSION),
      ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(sourceArticleUsage, ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_NO),
      compositePartUsageId
    );
    this.applyStrippedCompositeArticleInformation(targetArticleUsage, strippedCompositeInformation);
  }

  public static applyCompositeArticleInformation(articleUsage: ArticleUsageVO, compositeInformation: CompositeArticleInformation): void {
    if (!articleUsage || !compositeInformation) {
      return;
    }
    ArticleUsageUtils.pushArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID,
      compositeInformation.usageId
    );
    ArticleUsageUtils.pushArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID,
      compositeInformation.articleId
    );
    ArticleUsageUtils.pushArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION,
      compositeInformation.articleVersion
    );
    ArticleUsageUtils.pushArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO,
      compositeInformation.articleNo
    );
    if (compositeInformation.originalPartUsageId) {
      this.setCompositePartUsageIdToUsage(articleUsage, compositeInformation.originalPartUsageId);
    }
    if (compositeInformation.groupId) {
      articleUsage.groupId = compositeInformation.groupId;
    }
  }

  public static applyStrippedCompositeArticleInformation(articleUsage: ArticleUsageVO, strippedCompositeInformation: CompositeArticleInformation): void {
    if (!articleUsage || !strippedCompositeInformation) {
      return;
    }
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_USAGE_ID,
      strippedCompositeInformation.usageId
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID,
      strippedCompositeInformation.articleId
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_VERSION,
      strippedCompositeInformation.articleVersion
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_NO,
      strippedCompositeInformation.articleNo
    );
    if (strippedCompositeInformation.originalPartUsageId) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(
        articleUsage,
        ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_PART_USAGE_ID,
        strippedCompositeInformation.originalPartUsageId
      );
    }
  }

  public static copyCompositeArticleInformationStackFromUsage(
    sourceArticleUsage: ArticleUsageVO,
    targetArticleUsage: ArticleUsageVO,
    compositePartUsageId: string = null
  ): void {
    if (!sourceArticleUsage || !targetArticleUsage) {
      return;
    }
    if (!ArticleUsageUtils.isCompositePartArticleUsage(sourceArticleUsage, false)) {
      return;
    }
    this.copyArticleUsageInfoPropertyStringValues(sourceArticleUsage, targetArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID);
    this.copyArticleUsageInfoPropertyStringValues(sourceArticleUsage, targetArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_NO);
    this.copyArticleUsageInfoPropertyIntValues(sourceArticleUsage, targetArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID);
    this.copyArticleUsageInfoPropertyIntValues(sourceArticleUsage, targetArticleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION);
    if (compositePartUsageId) {
      this.setCompositePartUsageIdToUsage(targetArticleUsage, compositePartUsageId);
    }

    targetArticleUsage.groupId = sourceArticleUsage.groupId;
  }

  public static copyArticleUsageInfoPropertyStringValues(
    sourceArticleUsage: ArticleUsageVO,
    targetArticleUsage: ArticleUsageVO,
    propertyName: string
  ): void {
    const stringValues: string[] = ArticleUsageUtils.getArticleUsageInfoPropertyStringValues(sourceArticleUsage, propertyName);
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValues(targetArticleUsage, propertyName, stringValues);
  }

  public static copyArticleUsageInfoPropertyIntValues(sourceArticleUsage: ArticleUsageVO, targetArticleUsage: ArticleUsageVO, propertyName: string): void {
    const intValues: number[] = ArticleUsageUtils.getArticleUsageInfoPropertyIntValues(sourceArticleUsage, propertyName);
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValues(targetArticleUsage, propertyName, intValues);
  }

  public isCompositeArticleConnectedToRail(container: ArticleContainer): boolean {
    const baseArticleContainers: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(container);
    for (const baseArticleContainer of baseArticleContainers) {
      if (this.articleConnectionService.isArticleConnectedToMountingRail(baseArticleContainer)) {
        return true;
      }
    }
    return false;
  }

  public isCompositeMultipleBaseArticle(compositeArticle: ArticleVO): boolean {
    if (!ArticleUtils.isCompositeArticle(compositeArticle)) {
      return false;
    }

    const compositeType: string = this.getCompositeArticleType(compositeArticle);
    if (!compositeType) {
      // to support previously configured composite articles (before introducing compositeArticleType property)
      return !compositeArticle.baseArticle;
    } else {
      return compositeType === CompositeArticleUtils.COMPOSITE_TYPE_MULTIPLE_BASE;
    }
  }

  public static isCompositePartArticleExists(containers: ArticleContainer[]): boolean {
    if (!containers) {
      return false;
    }

    for (const container of containers) {
      if (ArticleUsageUtils.isCompositePartArticleUsage(container.articleUsage)) {
        return true;
      }
    }
    return false;
  }

  public groupContainsAllCompositePartArticlesConnectedToMountingRail(
    articleUsageIds: string[],
    articleContainer: ArticleContainer,
    useStrippedCompositeInfo: boolean = false
  ): boolean {
    const baseContainers: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(articleContainer, useStrippedCompositeInfo);
    const baseContainerIds: string[] = ArticleContainerUtils.extractArticleGUIDs(baseContainers);

    return (
      this.articleConnectionService.isArticleConnectedToMountingRail(baseContainers[0]) &&
      ArrayUtils.sourceArrayContainsAllElementsFromTargetArray(articleUsageIds, baseContainerIds)
    );
  }

  public isArticlesOfSameCompositeArticle(articles: ArticleContainer[]): boolean {
    if (!articles || articles.length === 0) {
      return false;
    }

    const firstCompositionId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(articles[0]);
    if (!firstCompositionId) {
      return false;
    }

    let currentCompositionId: string;
    for (let i: number = 1; i < articles.length; i++) {
      currentCompositionId = CompositeArticleInfoUtils.getCompositeArticleUsageId(articles[i]);
      if (firstCompositionId !== currentCompositionId) {
        return false;
      }
    }
    return true;
  }

}
